import React, { createContext, useContext, useEffect, useState } from "react";

import { getToken } from "../api/client";
import { getMyProfile, updateMyProfile } from "../api/services/candidate";
import { useSuperApp } from "../hooks/useSuperApp";

const STORAGE_KEY = "diil_account_role";
const ROLES = ["jobseeker", "employer"];

const isRole = (value) => ROLES.includes(value);

const readStoredRole = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    // Default to jobseeker — the role picker is skipped on login, so anyone who has
    // never chosen is treated as a jobseeker until they explicitly switch.
    return isRole(stored) ? stored : "jobseeker";
  } catch {
    return "jobseeker";
  }
};

/**
 * The account role outside React.
 *
 * Applying and interviewing are jobseeker actions, and the buttons that start them are
 * spread across the feed, the map, saved jobs, the preview drawer and the job page.
 * Hiding each one is necessary but not sufficient — one missed button, or a stale tab,
 * and an employer applies to a job. So the services that perform those actions check
 * the role here, which is the same value the provider reads and writes.
 */
export const getStoredAccountRole = () => readStoredRole();

/** True when the current account must not apply, send a CV, or sit an interview. */
export const isEmployerAccount = () => readStoredRole() === "employer";

/** The one message shown wherever an employer is turned away from those actions. */
export const EMPLOYER_BLOCKED_MESSAGE =
  "Ажил олгогч эрхээр энэ үйлдлийг хийх боломжгүй. Ажил горилогч болж солино уу.";

const writeStoredRole = (role) => {
  try {
    if (role) localStorage.setItem(STORAGE_KEY, role);
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* private mode */
  }
};

const RoleContext = createContext(undefined);

/**
 * The account role — jobseeker or employer — which decides where the "post" button
 * sends someone and whether they can post in the Ажлын Групп feed.
 *
 * It belongs to the account, not the browser: it is stored on the candidate row and read
 * back through /v1/chat/profile/me. localStorage is kept as a seed so the role resolves
 * synchronously on the first paint — Sidebar's employer-only "Миний зар" and the post
 * button's routing both read it during render, and hydrating asynchronously without a seed
 * would make them flicker on every page load.
 */
export const RoleProvider = ({ children }) => {
  const { isAuthenticated } = useSuperApp();
  const [accountRole, setAccountRoleState] = useState(readStoredRole);

  // Adopt the stored role once the profile is readable. Guarded on the token so a
  // signed-out visitor never fires a 401, which the API client would treat as an
  // expired session.
  useEffect(() => {
    if (!getToken()) return undefined;

    let cancelled = false;
    getMyProfile().then((res) => {
      if (cancelled || !res?.success) return;
      // getMyProfile unwraps the candidate; tolerate the raw envelope too
      const role = res.data?.accountRole ?? res.data?.candidate?.accountRole;
      if (isRole(role)) {
        setAccountRoleState(role);
        writeStoredRole(role);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  /**
   * Switches role immediately and tells the server after. Deliberately optimistic:
   * changing role is a UI affordance people hit mid-task — from the group composer, for
   * instance — and it should not stall or fail on a slow network. A failed write leaves
   * the local value in place, and the next successful profile read reconciles it.
   */
  const setAccountRole = (role) => {
    setAccountRoleState(role);
    writeStoredRole(role);

    if (role && getToken()) {
      updateMyProfile({ accountRole: role });
    }
  };

  return (
    <RoleContext.Provider value={{ accountRole, setAccountRole }}>
      {children}
    </RoleContext.Provider>
  );
};

export const useAccountRole = () => {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error("useAccountRole must be used within a RoleProvider");
  return ctx;
};
